/** Ask a question with choices: two to four answers, one tap each, one vote per person. */
import { useState } from "react";
import { Button, IconButton } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { ANSWER_MAX, QUESTION_MAX, readyQuestion } from "./questions";

const PLACEHOLDERS = ["This spring", "Later this year", "Just looking", "Not sure yet"];
const QUESTION_PLACEHOLDER = "When are you hoping to move?";

export function AskSheet({ open, to, onClose, onSend }: {
  open: boolean;
  /** Who it goes to: "everyone in the room", "the circle". */
  to: string;
  onClose: () => void;
  /** Sends it; a rejection keeps the sheet open with what was written. */
  onSend: (q: { text: string; options: string[] }) => Promise<void>;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [opts, setOpts] = useState(["", ""]);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const q = readyQuestion(text, opts);
    if ("error" in q) {
      toast({ text: q.error, icon: "info" });
      if (!text.trim()) document.getElementById("cq-t")?.focus();
      return;
    }
    setBusy(true);
    try {
      await onSend(q);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Ask ${to} a question`}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="send" onClick={send} disabled={busy}>
            {busy ? "Sending..." : "Send the question"}
          </Button>
        </>
      }
    >
      <div className="sheet-b col" style={{ gap: 16 }}>
        <div className="field">
          <label htmlFor="cq-t">Your question</label>
          <input dir="auto" id="cq-t" className="inp" maxLength={QUESTION_MAX} placeholder={QUESTION_PLACEHOLDER} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="field">
          <div className="lbl">Answers <bdi>{to}</bdi> can tap</div>
          <div className="cq-opts">
            {opts.map((o, i) => (
              <div className="cq-o" key={i}>
                <span className="cq-n">{i + 1}</span>
                <input dir="auto"
                  className="inp"
                  maxLength={ANSWER_MAX}
                  placeholder={PLACEHOLDERS[i]}
                  aria-label={`Answer ${i + 1}`}
                  value={o}
                  onChange={(e) => setOpts(opts.map((x, j) => (j === i ? e.target.value : x)))}
                />
                {opts.length > 2 ? <IconButton icon="x" small label="Take this answer off" onClick={() => setOpts(opts.filter((_, j) => j !== i))} /> : null}
              </div>
            ))}
          </div>
          {opts.length < 4 ? (
            <button type="button" className="link cq-add" onClick={() => setOpts([...opts, ""])}>
              <Icon name="plus" /> Add an answer
            </button>
          ) : (
            <div className="hint">Four at most</div>
          )}
        </div>
      </div>
    </Sheet>
  );
}
